/**
 * Post Event Bus - Synchronizes post state changes across all components
 *
 * This event bus ensures that when a post is liked, disliked, or updated on one page/component,
 * all other instances of that post are immediately updated without needing to refresh.
 */

type PostEventType =
  | 'post:like'
  | 'post:unlike'
  | 'post:dislike'
  | 'post:undislike'
  | 'post:update'
  | 'post:delete';

interface PostLikeEvent {
  postId: string;
  liked: boolean;
  likes: number;
  userId?: string;
}

interface PostDislikeEvent {
  postId: string;
  disliked: boolean;
  dislikes: number;
  userId?: string;
}

interface PostUpdateEvent {
  postId: string;
  updates: Partial<{
    title?: string;
    content?: string;
    mediaUrl?: string;
    likes?: number;
    dislikes?: number;
    liked?: boolean;
    disliked?: boolean;
  }>;
}

interface PostDeleteEvent {
  postId: string;
}

type PostEventData =
  | PostLikeEvent
  | PostDislikeEvent
  | PostUpdateEvent
  | PostDeleteEvent;

type EventListener = (data: PostEventData) => void;

class PostEventBus {
  private listeners: Map<PostEventType, Set<EventListener>> = new Map();

  /**
   * Subscribe to post events
   */
  on(event: PostEventType, callback: EventListener): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }

    this.listeners.get(event)!.add(callback);

    // Return unsubscribe function
    return () => {
      this.off(event, callback);
    };
  }

  /**
   * Unsubscribe from post events
   */
  off(event: PostEventType, callback: EventListener): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.delete(callback);
    }
  }

  /**
   * Emit a post event to all subscribers
   */
  emit(event: PostEventType, data: PostEventData): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
        }
      });
    }
  }

  /**
   * Emit a like event
   */
  emitLike(postId: string, liked: boolean, likes: number, userId?: string): void {
    this.emit(liked ? 'post:like' : 'post:unlike', {
      postId,
      liked,
      likes,
      userId
    });
  }

  /**
   * Emit a dislike event
   */
  emitDislike(postId: string, disliked: boolean, dislikes: number, userId?: string): void {
    this.emit(disliked ? 'post:dislike' : 'post:undislike', {
      postId,
      disliked,
      dislikes,
      userId
    });
  }

  /**
   * Emit a post update event
   */
  emitUpdate(postId: string, updates: Partial<any>): void {
    this.emit('post:update', {
      postId,
      updates
    });
  }

  /**
   * Emit a post delete event
   */
  emitDelete(postId: string): void {
    this.emit('post:delete', {
      postId
    });
  }

  /**
   * Clear all listeners (useful for cleanup/testing)
   */
  clear(): void {
    this.listeners.clear();
  }
}

// Export singleton instance
export const postEventBus = new PostEventBus();

// Export types for use in components
export type {
  PostEventType,
  PostLikeEvent,
  PostDislikeEvent,
  PostUpdateEvent,
  PostDeleteEvent,
  PostEventData,
  EventListener
};
